import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import { FormattedMessage, useIntl, FormattedDate, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import IconBookMark from '../../containers/WishlistPage/IconBookMark';
import { updateListingGeneral } from '../../util/api';
import { useHistory, useLocation } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { updateTotalLikes } from '../../util/api';

import { ReactComponent as ProtectionIcon } from '../OrderPanel/ProductOrderForm/protectionIcon.svg';
import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCardComponent = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    isProfilePage = false,
    pageSearchParams,
    onUpdateProfile,
    currentUser,
  } = props;

  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();


  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const { isCompleted, soldAt, soldPrice } = publicData || {};
  const hasSoldData =
    isCompleted === true &&
    typeof soldAt === 'number' &&
    typeof soldPrice === 'number';

  const soldDate = hasSoldData ? new Date(soldAt) : null;
  const soldPriceFormatted = hasSoldData
    ? `$${Number(soldPrice).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : null;

  const isRecentlySoldListings = !!(pageSearchParams && pageSearchParams?.pub_availability &&
    pageSearchParams?.pub_availability === 'sold');

  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const authorId = currentListing?.author?.id;

  const isOwnListing = currentUser && authorId && currentUser?.id?.uuid === authorId?.uuid;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  // const currentUser = useSelector(state => state.user.currentUser);

  const handleListingClick = () => {
    // we should not add views if the user is the owner of the listing
    if (isOwnListing) {
      return;
    }

    const clicks = currentListing?.attributes?.publicData?.totalClicks || 0;
    updateListingGeneral({
      params: {
        id: currentListing.id,
        publicData: { totalClicks: Number(clicks + 1) },
      },
    });
  };

  const isLiked = () => {
    const likedListings = currentUser && currentUser?.attributes?.profile?.publicData.wishlist;
    return likedListings && likedListings.includes(currentListing.id.uuid);
  };

  const handleFavorateClick = (event) => {
    event.preventDefault();
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
      return;
    }
    const listingId = currentListing?.id?.uuid;
    const likedListings = currentUser && currentUser?.attributes?.profile.publicData.wishlist;
    const currentlyLiked = likedListings && likedListings.includes(listingId);
    const action = currentlyLiked ? "unlike" : "like";
    let newListings = likedListings ? likedListings : [];
    if (isLiked()) {
      newListings = likedListings.filter(l => l !== currentListing?.id?.uuid);
    } else {
      newListings.push(currentListing?.id?.uuid);
    };
    onUpdateProfile({
      publicData: {
        wishlist: newListings,
      },
    })
      .then(() => {
        return updateTotalLikes({
          listingId,
          action,
        });
      })
      .catch(e => {
        console.error("Failed updating favourites or like count", e);
      });
  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }} onClick={handleListingClick}>
      {/* <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={9}
        height={16}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper> */}
      <div className={css.imgWrapper}>
        <img
          alt={title}
          style={{ width: isProfilePage ? '65%' : '100%' }}
          src={firstImage?.attributes?.variants?.default?.url}
        />
        {!isOwnListing && (
          <div
            className={css.iconContainer}
            onClick={handleFavorateClick}
          >
            <IconBookMark liked={isLiked()} />
          </div>
        )}

      </div>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.priceAndRating}>
            {isRecentlySoldListings ?
              null
              : <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />}
          </div>
          {/* {isOwnListing ? null : <div className={css.iconContainer} onClick={handleFavorateClick}>
            <IconBookMark liked={isLiked()} />
          </div>} */}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
          {hasSoldData ? (
            <div className={css.soldInfo}>
              <div className={css.soldPrice}>{soldPriceFormatted}</div>
              <div className={css.soldDate}>
                <FormattedDate
                  value={soldDate}
                  year="numeric"
                  month="short"
                  day="2-digit"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className={css.protectionBadge}>
        <ProtectionIcon className={css.protectionBadgeIcon} />
        <span className={css.protectionBadgeText}>Purchase Protection</span>
      </div>
    </NamedLink>
  );
};
const mapStateToProps = (state) => {
  const {
    currentUser,
  } = state.user;


  return {
    currentUser,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onUpdateProfile: data => dispatch(updateProfile(data)),
  };
};

const ListingCard = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ListingCardComponent);

export default ListingCard;
